import React, { useEffect, useState, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { StyledJoyLogo, StyledCanvasWrapper, StyledCanvas } from './ParticleJoyLogo.styles';
import type { ParticleEffectManagerType } from './ParticleEffect';
import { particleEffect } from './ParticleEffect';
import type { ParticleJoyLogoMark } from './ParticleJoyLogo.types';

/** The XS mark from the brand kit (`joy_logo_XS_black.svg`, viewBox 27×17), scaled to the
 *  `objectBoundingBox` unit square so it fills the logo box at any size. */
const XS_MARK_PATH =
  'M3.4,13.03s.04-.05.07-.06c.87-.42,1.62-1.69,2.39-4.97l.34-1.48.89-3.9C7.79-.54,10.22-.08,10.98.2c.02,0,.04.02.05.04s.02.04.03.06,0,.05,0,.07-.02.04-.03.06c-.26.29-.56,1.02-.97,2.75l-1.31,5.43c-.84,3.52-3.5,4.49-5.22,4.62-.03,0-.07,0-.1-.02s-.05-.05-.06-.08,0-.07.01-.1h0ZM26.03,3.65c1.06.34,1.05,1.48.89,2.21-.27,1.15-.71,2.25-1.3,3.28-.02.03-.04.05-.07.06s-.06.01-.09,0-.06-.03-.07-.06-.02-.06-.01-.09c.18-1.18-.05-2.4-.66-3.43-.84-1.39.57-2.22,1.33-1.97ZM18.78,4.69c.8-.8,3.51-2.32,4.49,1.78.39,1.54.37,3.16-.06,4.7-.42,1.53-1.24,2.92-2.37,4.04-.02.02-.05.03-.08.03s-.06,0-.08-.02c-.03-.01-.05-.04-.06-.06-.01-.03-.01-.06,0-.09.78-2.97.3-10.08-1.75-10.15-.03,0-.05-.01-.07-.03s-.04-.04-.05-.06-.01-.05,0-.08.02-.05.04-.07h0ZM19.41,16.1s.06,0,.09.01.05.04.06.07.01.06,0,.09-.03.06-.06.07c-.61.45-1.35.68-2.11.65-1.99-.09-2.07-1.67-1.49-2.42.31-.37.74-.63,1.2-.74.02,0,.05,0,.07,0s.04.02.05.04.02.04.03.06,0,.05,0,.07c-.26.88.41,2.4,2.15,2.09h0ZM11.22,5.25c1.09-.95,2.48-1.48,3.93-1.49,2.25,0,3.87,1.45,3.87,3.68-.03.82-.22,1.63-.57,2.37s-.85,1.41-1.46,1.95c-1.06.94-2.43,1.47-3.85,1.47-2.33,0-3.93-1.47-3.93-3.72.04-.81.23-1.61.58-2.34s.83-1.39,1.43-1.93h0ZM13.56,12.37c1.23-.65,2.06-3.83,2.19-4.35.18-.61.7-4.28-1.06-3.41-1.18.6-1.99,3.58-2.18,4.28l-.02.07c-.22.88-.72,4.34,1.06,3.41h0ZM.48,12.08s.04.04.06.05.05.01.08,0,.05-.02.07-.04.03-.05.03-.07c.03-.57.64-1.26,1.68-1.18.73.06,1.11-1.12.5-1.74-.75-.77-1.87-.47-2.43.34C-.3,10.54,0,11.48.48,12.08h0Z';
const XS_MARK_TRANSFORM = 'scale(0.037037, 0.058824)'; // 1/27, 1/17

interface ParticleJoyLogoProps {
  style?: React.CSSProperties;
  id?: string;
  href?: string;
  className?: string;
  logoColor: string;
  onClick?: () => void;
  /** Accessible name, applied only when `href` makes this a real link. Defaults to the brand
   *  name, which is not localized. Pass a translated, more descriptive label from consumers that
   *  have `tq` available. */
  ariaLabel?: string;
  /** Which cut of the mark to clip the particle canvas to. Defaults to `default`. */
  mark?: ParticleJoyLogoMark;
}

export interface ParticleJoyLogoHandle {
  /**
   * Trigger particle animation at specified coordinates.
   * If no coordinates provided, triggers at a random position within the logo.
   */
  triggerParticles: (x?: number, y?: number) => void;
}

export const ParticleJoyLogo = forwardRef<ParticleJoyLogoHandle, ParticleJoyLogoProps>(
  ({ id, style, href, className, logoColor, onClick, ariaLabel = 'Joy', mark = 'default' }, ref) => {
    const particleRef = useRef<HTMLCanvasElement>(null);
    const particleManager = useRef<ParticleEffectManagerType | null>(null);
    const [isParticleRunning, setIsParticleRunning] = useState(false);
    const handleLogoClicked = useCallback(() => {
      onClick?.();
    }, [onClick]);

    useEffect(() => {
      if (id && particleRef.current) {
        const particleEl = particleEffect(particleRef.current, logoColor);
        particleEl?.changeBgColor(logoColor);
        particleManager.current = particleEl;
      }
      return () => {
        particleManager?.current?.removeParticles();
      };
    }, [id, particleRef, logoColor]);

    const triggerParticlesInternal = useCallback(
      (x?: number, y?: number) => {
        if (id && particleManager?.current?.initParticles && !isParticleRunning) {
          // Default to random position within canvas bounds if not specified
          const posX = x ?? Math.random() * 54;
          const posY = y ?? Math.random() * 35;
          particleManager.current.initParticles(40, posX, posY);
          setIsParticleRunning(true);
          setTimeout(() => setIsParticleRunning(false), 300);
        }
      },
      [id, isParticleRunning],
    );

    useImperativeHandle(ref, () => ({
      triggerParticles: triggerParticlesInternal,
    }));

    const onHandleParticle = (e: React.MouseEvent) => {
      triggerParticlesInternal(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    };

    // Without an href this is decorative, so it renders a span: an href-less <a> is still an
    // anchor to the HTML parser, which breaks when a host wraps the logo in its own link or button
    // (the admin header's menu trigger, the burger drawer's home link). The link-only attributes
    // ride along with the anchor: aria-label in particular is prohibited on the generic-role span,
    // so naming that would trade one axe violation for another.
    const elementProps = href ? ({ href, target: '_blank', 'aria-label': ariaLabel } as const) : ({ as: 'span' } as const);

    return (
      <StyledJoyLogo
        {...elementProps}
        id="menu2brand"
        data-testid="particle-logo"
        style={style}
        className={className}
        onMouseMove={onHandleParticle}
        onClick={handleLogoClicked}
        color={logoColor}
      >
        <div style={{ position: 'relative' }}>
          <svg
            viewBox="0 0 54 34"
            aria-hidden
            focusable={false}
          >
            <defs>
              <clipPath
                id={`${id}-clip-path`}
                clipPathUnits="objectBoundingBox"
              >
                {mark === 'xs' ? (
                  // No `clip-path` class: that class carries the default mark's CSS scale, which
                  // would override this path's own unit-square transform.
                  <path
                    d={XS_MARK_PATH}
                    transform={XS_MARK_TRANSFORM}
                    clipRule="evenodd"
                  />
                ) : (
                  <path
                    className="clip-path"
                    d="M11.71 44.44a.47.47 0 0 1 .251-.221c3.007-1.428 5.568-5.77 8.22-16.968l1.158-5.055L24.4 8.9C26.834-1.85 35.204-.274 37.833.697a.47.47 0 0 1 .292.329c.018.074.018.152-.001.226a.47.47 0 0 1-.107.199c-.88.994-1.92 3.474-3.34 9.38l-4.493 18.534c-2.893 12.01-12.062 15.323-17.996 15.757a.47.47 0 0 1-.518-.349.47.47 0 0 1 .039-.332zm77.964-31.975c3.647 1.165 3.602 5.05 3.064 7.553-.944 3.926-2.453 7.694-4.482 11.186a.47.47 0 0 1-.571.211.47.47 0 0 1-.298-.531c.636-4.044-.173-8.183-2.287-11.69-2.904-4.753 1.978-7.564 4.573-6.73zm-24.982 3.53c2.767-2.72 12.085-7.918 15.458 6.067 1.335 5.265 1.268 10.787-.197 16.018s-4.274 9.986-8.15 13.794a.48.48 0 0 1-.56.056.48.48 0 0 1-.229-.513c2.698-10.135 1.018-34.393-6.037-34.622a.47.47 0 0 1-.398-.309.47.47 0 0 1 .112-.491zm2.15 38.93a.47.47 0 0 1 .532.284.47.47 0 0 1-.189.573 11.37 11.37 0 0 1-7.26 2.205c-6.86-.31-7.146-5.713-5.134-8.25a7.88 7.88 0 0 1 4.15-2.525.47.47 0 0 1 .41.163.47.47 0 0 1 .1.204c.016.075.014.153-.008.227-.903 3.005 1.418 8.18 7.398 7.12zm-28.195-37a21.01 21.01 0 0 1 13.526-5.096c7.763 0 13.33 4.96 13.33 12.57-.097 2.806-.768 5.562-1.97 8.1a20.57 20.57 0 0 1-5.026 6.652c-3.666 3.225-8.38 5.01-13.263 5.027-8.026 0-13.537-5.027-13.537-12.706a20.74 20.74 0 0 1 1.999-7.974c1.192-2.498 2.872-4.732 4.94-6.572zm8.072 24.292c4.23-2.228 7.09-13.06 7.558-14.854.606-2.08 2.424-14.59-3.636-11.643-4.076 2.03-6.853 12.227-7.5 14.604l-.03.11-.04.142c-.766 3.017-2.47 14.797 3.647 11.643zm-45.082-.994a.47.47 0 0 0 .502.177.47.47 0 0 0 .344-.406c.114-1.942 2.207-4.285 5.785-4.022 2.527.194 3.82-3.816 1.726-5.93-2.584-2.617-6.426-1.588-8.358 1.143-2.675 3.782-1.635 7.004 0 9.038z"
                    fill="currentColor"
                  />
                )}
              </clipPath>
              <clipPath id="A">
                <path
                  className="clip-path"
                  fill="#fff"
                  d="M0 0h93v58H0z"
                />
              </clipPath>
            </defs>
          </svg>
          {id && (
            <StyledCanvasWrapper clipPathId={`${id}-clip-path`}>
              <StyledCanvas
                ref={particleRef}
                width="54px"
                height="35px"
              />
            </StyledCanvasWrapper>
          )}
        </div>
      </StyledJoyLogo>
    );
  },
);

ParticleJoyLogo.displayName = 'ParticleJoyLogo';
